import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, input, model, OnChanges, signal, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';

import { BaseControlComponent } from '@base/base-control.component';
import { Person, Project, SM, sortStringsBy, stopEvent } from '@models';
import { StoreService } from '@services/store.service';
import { ProjectBadgeComponent } from '@shared/_views/project-badge.component';

@Component({
    selector: 'vz-project-select',
    template: `
        <nz-select style="width: 100%"
            [class.pe-none]="readonly()"
            [nzShowArrow]="!readonly()"
            [formControl]="control"
            [nzMode]="multiple() ? 'multiple' : 'default'"
            [nzMaxMultipleCount]="multiple() ? 10 : INF"
            [nzCustomTemplate]="tplSelectItem"
            [nzPlaceHolder]="placeholder()"
            [nzDisabled]="vzDisabled()"
            [nzVariant]="readonly() ? 'borderless' : vzVariant()"
            [nzDropdownRender]="showIsMember() ? isMemberTpl : null"
            nzShowSearch
            [nzAllowClear]="allowClear()">
            @for (it of allItems(); track it.id) {
                <nz-option nzCustomContent [nzLabel]="it.name" [nzValue]="it.id">
                    <vz-project-badge [guid]="it.id" size="14" />
                    <span class="ml05">{{ it.name }}</span>
                </nz-option>
            }
        </nz-select>
        @if (showClearButton() && allowClear() && !vzDisabled() && ((!multiple() && control.value)) || (multiple() && control.value?.length)) {
            <button class="ant-btn-icon-only ant-btn-right" nz-button [nzType]="vzVariant() == 'outlined' ? 'default' : 'text'" (click)="se($event); control.setValue(multiple() ? [] : null)" animate.enter="vza-show-w" animate.leave="vza-hide-w">
                <i class="vzi-cross txt-err"></i>
            </button>
        }
        <ng-template #tplSelectItem let-item>
            <vz-project-badge [guid]="item.nzValue" size="14" />
            <span class="ml05">{{ itemsMap()[item.nzValue].name }}</span>
        </ng-template>
        <ng-template #isMemberTpl>
            <div>
                <div class="bc" style="border-bottom: 1px solid; height: 8px;"></div>
                <label class="ph05 pt05 no-wrap"
                    nz-checkbox
                    [(nzChecked)]="iAmMember"
                    (nzCheckedChange)="filterItems()">
                    Только проекты где я участник
                </label>
            </div>
        </ng-template>
    `,
    styles: [':host { display: flex; flex: 1; flex-basis: 0px; }'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: ProjectSelectComponent
        }
    ],
    imports: [
        CommonModule, ReactiveFormsModule,
        NzSelectModule, NzButtonModule, NzCheckboxModule,
        ProjectBadgeComponent
    ]
})
export class ProjectSelectComponent extends BaseControlComponent implements OnChanges {
    placeholder         = input<string>('Выберите проект');
    allowedIds          = input<string[]>();
    vzDisabled          = input(false, { transform: booleanAttribute });
    allowClear          = input(false, { transform: booleanAttribute });
    showClearButton     = input(true, { transform: booleanAttribute });
    multiple            = input(false, { transform: booleanAttribute });
    forTaskCreate       = input(false, { transform: booleanAttribute });
    readonly            = input(false, { transform: booleanAttribute });
    iAmMember           = model<boolean>(false);
    showIsMember        = input(false, { transform: booleanAttribute });

    itemsMap            = signal<SM<Project>>({});
    allItems            = signal<Project[]>([]);

    INF = Infinity;
    se = stopEvent;
    isMember = (u: Person | undefined, p: Project) => {
        if (!this.iAmMember() || !u) {
            return true;
        }
        for (const id of p.groupIds) {
            if (u._hasGroup(this.activeOrgId, id)) {
                return true;
            }
        }
        return p.personIds.includes(u.id || '');
    }

    constructor(protected _store: StoreService) {
        super(_store);
        this._S.items = this._store.state('projects').subscribe(() => this.filterItems());
        let vs = false;
        this.control.valueChanges.subscribe(v => {
            if (v && !vs) {
                vs = true;
                this.filterItems();
            }
        });
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.filterItems();
    }

    vzOnActiveOrgChange(): void {
        this.filterItems();
    }

    filterItems(): void {
        const tm = this._store.getState('projects').items;
        const user = this._store.getState('user').user;
        this.itemsMap.set(tm);
        const allItems = Object.values(this.itemsMap()).filter(item => item.orgId == this.activeOrgId).sort(sortStringsBy('name'));
        // console.log('=[ProjectSelectComponent]===> filterItems, control.value:', this.control.value, 'allItems:', allItems);
        if (this.granted['task.create'] || !this.forTaskCreate()) {
            this.allItems.set(allItems.filter(p => (!this.allowedIds() || this.allowedIds()?.includes(p.id!)) && (this.isMember(user, p) || this.control.value == p.id)));
        }
        else {
            this.allItems.set(allItems.filter(p => this.pGranted[p.id!] && this.pGranted[p.id!]['task.create'] && (!this.allowedIds() || this.allowedIds()?.includes(p.id!)) && (this.isMember(user, p) || this.control.value == p.id)));
        }
        // console.log('=[ProjectSelectComponent]===> filterItems, this.allItems:', this.allItems());
        if (this.multiple()) {
            const pids = (Array.isArray(this.control.value) ? this.control.value : []).filter(pid => this.allItems().findIndex(p => p.id == pid) != -1);
            this.control.setValue(pids);
        }
        else {
            const v = this.control.value;
            if (typeof v != 'string' || this.allItems().findIndex(p => p.id == v) == -1) {
                this.control.setValue(null);
            }
        }
    }

}
